import {
  OnePageCheckoutMachineContext,
  SignupEvent,
  OnePageCheckoutMachineEvent,
} from './OnePageCheckout.machine';
import { createHeaders } from '@/utils/createHeaders';
import { PostHog } from 'posthog-js';
import { calculateSessionCost } from '@/utils/calculateSessionCost';

const makeCreatePendingSession =
  (posthog: PostHog) =>
  async (
    context: OnePageCheckoutMachineContext,
    event: OnePageCheckoutMachineEvent
  ) => {
    // Type guard to ensure we have a SIGNUP event
    if (event.type !== 'SIGNUP') {
      throw new Error(
        'createPendingSession can only be called with SIGNUP event'
      );
    }

    const signupEvent = event as SignupEvent;
    const { slot, duration, mentor_info, access_token } = context;

    const given_name = signupEvent.data.given_name;
    const family_name = signupEvent.data.family_name;

    // Calculate the final session cost with discounts applied
    const sessionCostResult = calculateSessionCost({
      duration: duration as 30 | 60,
      originalHourlyRate: mentor_info.original_hourly_rate,
      mentorSlug: mentor_info.slug,
      discountCode: mentor_info.discount_code,
    });

    // Convert final cost from dollars to cents for Stripe
    const finalCostInCents = Math.round(
      sessionCostResult.finalCost * 100
    );

    const requestBody = {
      email: signupEvent.data.email,
      given_name,
      family_name,
      slot,
      duration,
      mentor_id: mentor_info.mentor_id,
      cost: finalCostInCents, // Send final calculated cost in cents
      discount_code: mentor_info.discount_code,
    };

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/one_page_checkout/pending_session`,
      {
        headers: createHeaders(access_token),
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      console.error(error);
      throw new Error(error.message);
    }

    const { elapsed_time, pending_session_id, stripe_client_secret } =
      await response.json();

    posthog.capture('one_page_checkout: pending session created', {
      elapsed_time,
      pending_session_id,
    });

    return {
      stripe_client_secret,
      pending_session_id,
      family_name,
      given_name,
    };
  };

export { makeCreatePendingSession };
